import React, { useState, useEffect } from 'react';
import { client, urlFor } from '../../client'; // Import Sanity client and urlFor
import { PortableText } from '@portabletext/react'; // Import PortableText

// Consistent Loading Spinner
const LoadingSpinner = () => (
  <div className="flex justify-center items-center min-h-screen">
    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-[#2a4865]"></div>
    <p className="ml-4 text-lg text-gray-700">Loading...</p>
  </div>
);

// Consistent Error Message
const ErrorMessage = ({ message }) => (
  <div className="container mx-auto px-4 py-12 text-center">
    <p className="text-red-600 font-semibold">{message || 'Error loading page content. Please try again later.'}</p>
  </div>
);

// Custom Portable Text components to match original styling for links
const ptComponents = {
  marks: {
    link: ({ children, value }) => {
      const rel = !value.href.startsWith('/') ? 'noopener noreferrer' : undefined;
      // Style links based on their type (mailto or http/https)
      const isMail = value.href.startsWith('mailto:');
      const isDiscoverDycd = value.href.includes('discoverdycd.dycdconnect.nyc');
      
      let className = 'text-blue-600 hover:underline'; // Default link style
      if (isDiscoverDycd) {
        className = 'text-yellow-400 hover:underline'; // Style for registration link
      } else if (isMail) {
        className = 'text-[#691e31] hover:text-[#8a2c43] transition-colors ml-1'; // Style for email link
      }
      
      return (
        <a href={value.href} target="_blank" rel={rel} className={className}>
          {children}
        </a>
      );
    },
  },
  // Can add block styles here if needed, but currently handled by parent divs
};

const AfterschoolNIA = () => {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPageData = async () => {
      setLoading(true);
      try {
        const query = `*[_type == "afterSchoolNiaPage" && _id == "afterSchoolNiaPageContent"][0] {
          bannerTitle,
          bannerImage {
            asset->{
              _id,
              url
            }
          },
          registrationLink { text, url },
          importantInfoTitle,
          importantInfoItems,
          howToRegisterTitle,
          howToRegisterSteps,
          whatToExpectTitle,
          whatToExpectItems
        }`;
        const data = await client.fetch(query);
        if (data) {
          setPageData(data);
        } else {
          setError('After School NIA Page content not found.');
        }
        setLoading(false);
      } catch (err) {
        console.error("Failed to fetch After School NIA page data:", err);
        setError(err.message || 'Failed to load page content. Please try again later.');
        setLoading(false);
      }
    };

    fetchPageData();
  }, []);

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} />;
  if (!pageData) return <ErrorMessage message="No content available for this page." />;

  return (
    <div className="bg-white">
      {/* Hero Banner with Image Overlay - Dynamic */}
      <section className="relative h-[40vh] max-h-[400px]">
        <div className="w-full h-full relative overflow-hidden">
          {pageData.bannerImage?.asset?.url ? (
            <img 
              src={urlFor(pageData.bannerImage).url()} 
              alt={pageData.bannerTitle || 'PS52 Afterschool Program'} 
              className="absolute inset-0 w-full h-full object-cover object-center" 
              loading="lazy"
            />
          ) : (
            // Fallback if no image is set
            <div className="absolute inset-0 w-full h-full bg-gray-300 flex items-center justify-center">
              <span className="text-gray-500">Banner Image Missing</span>
            </div> 
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-black/20"></div>
        </div>
        
        <div className="absolute inset-0 flex items-end">
          <div className="w-full px-6 md:px-12 lg:px-16">
            <div className="max-w-3xl pb-12 md:pb-16 lg:pb-16 text-left">
              <div className="w-16 h-1 bg-yellow-500 mb-6"></div>
              
              <h1 className="text-white text-4xl md:text-5xl font-bold mb-2 leading-tight text-left">
                {pageData.bannerTitle || 'Afterschool NIA'} {/* Dynamic Title */}
              </h1>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="bg-white p-8 rounded-lg shadow-md max-w-4xl mx-auto">
            <div className="space-y-8">
              {/* Registration Link - Dynamic */}
              {pageData.registrationLink?.url && pageData.registrationLink?.text && (
                <section className="bg-[#2a4865]/5 p-6 rounded-lg border-l-4 border-[#2a4865]">
                  <h3 className="text-xl font-bold text-[#2a4865] mb-4 relative overflow-hidden pb-2">
                    Links
                    <span className="absolute bottom-0 left-0 w-12 h-0.5 bg-yellow-500"></span>
                  </h3>
                  <div className="space-y-3">
                    <a 
                      href={pageData.registrationLink.url}
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-block bg-[#691e31] text-white py-3 px-6 rounded-full hover:bg-[#7f2a3e] transition-colors font-medium"
                    >
                      {pageData.registrationLink.text}
                    </a>
                  </div>
                </section>
              )}
              
              {/* Important Information - Dynamic */}
              {pageData.importantInfoTitle && pageData.importantInfoItems?.length > 0 && (
                <section>
                  <h3 className="text-xl font-bold text-[#2a4865] mb-4 relative overflow-hidden pb-2">
                    {pageData.importantInfoTitle}
                    <span className="absolute bottom-0 left-0 w-12 h-0.5 bg-yellow-500"></span>
                  </h3>
                  <ul className="list-none space-y-3 mb-4">
                    {pageData.importantInfoItems.map((item, index) => (
                      <li key={index} className="flex items-start">
                        {/* Retain exact SVG icon */}
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 mt-0.5 text-yellow-500 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
              
              {/* How To Register - Dynamic */}
              {pageData.howToRegisterTitle && pageData.howToRegisterSteps?.length > 0 && (
                <section className="bg-[#2a4865] p-6 rounded-lg text-white">
                  <h3 className="text-xl font-bold mb-4 relative overflow-hidden pb-2 flex items-center">
                    {/* Retain exact title styling */}
                    <span className="w-1 h-6 bg-yellow-500 mr-3"></span>
                    <span className="text-white">{pageData.howToRegisterTitle}</span>
                  </h3>
                  <ol className="list-none space-y-3 mb-4">
                    {pageData.howToRegisterSteps.map((step, index) => (
                      <li key={step._key || index} className="flex items-start">
                        {/* Retain exact number circle styling */}
                        <div className="bg-yellow-500 text-[#2a4865] rounded-full h-6 w-6 flex items-center justify-center font-bold mr-3 mt-0.5 flex-shrink-0">
                          {index + 1}
                        </div>
                        {/* Render step content using PortableText with custom components */}
                        <span className="text-white"> {/* Ensure text remains white */} 
                           <PortableText value={[step]} components={ptComponents} /> 
                        </span>
                      </li>
                    ))}
                  </ol>
                </section>
              )}
              
              {/* What To Expect Next - Dynamic */}
              {pageData.whatToExpectTitle && pageData.whatToExpectItems?.length > 0 && (
                <section>
                  <h3 className="text-xl font-bold text-[#2a4865] mb-4 relative overflow-hidden pb-2">
                    {pageData.whatToExpectTitle}
                    <span className="absolute bottom-0 left-0 w-12 h-0.5 bg-yellow-500"></span>
                  </h3>
                  <ul className="list-none space-y-3">
                    {pageData.whatToExpectItems.map((item, index) => (
                      <li key={item._key || index} className="flex items-start">
                         {/* Retain exact SVG icon */}
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 mt-0.5 text-[#691e31] flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {/* Render item content using PortableText with custom components */}
                        <span className="text-gray-700">
                            <PortableText value={[item]} components={ptComponents} />
                        </span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AfterschoolNIA; 